// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
import React, {useCallback, useState, useMemo} from 'react';
import cn from 'classnames';
import {useStore} from '@nanostores/react';
import {dur_to_str} from '/util/date.js';
import etask from '/util/etask.js';
import {$is_root} from '../../store/user.js';
import store from '../../store.js';
import {fmt_money, bw_to_readable, limit_txt} from '../../utils/misc.js';
import * as api from '../../api.js';
import {BILLING, UPTIME_BILLING, APP_ID,
    APP_TITLE} from '../../utils/constants.js';
import {Text} from '../../comp/text';
import {Countdown} from '../../comp/countdown';
import {TH, TR, TD, Table} from '../../comp/table';
import {Icon} from '../../comp/icon';
import {Device} from '../device';
import {Popup} from '../../comp/popup';
import {Context_menu} from '../../comp/context_menu';
import {Checkbox} from '../../comp/checkbox';
import {Country} from '../../comp/country';
import {alert} from '../../comp/alert';
import {Raw_mongo_device} from './raw_mongo_device.js';
import {Edit_device} from './edit_device.js';
import {Status_dot} from './status_dot.js';
import {use_device_statuses, use_list_pagination} from './hooks.js';
import {hide_device, unhide_device, delete_device} from './device_actions.js';
import './devices_table.less';

const NAME_LEN_LIMIT = 20;
const ACTION_TYPES = {
    RENAME: 'rename',
    HIDE: 'hide',
    UNHIDE: 'unhide',
    DELETE: 'delete',
    VIEW_OBJECT: 'view_object',
};

function Undefined_status(){
    const tip = <Text.xs>
      Data is not available yet. If you have just installed the device,
      the data will be available in{' '}
      <Countdown counter="balance_sync" units={1} size="xs"/>
    </Text.xs>;
    return <Icon ico="faq" color="main" tip={tip} size="xxs"/>;
}

const get_title = d=>
    `${limit_txt(d.title, NAME_LEN_LIMIT)}${d.hide_ts ? ' (hidden)' : ''}`;

const get_rate = d=>{
    let content = <Undefined_status/>;
    let tip = '';
    if (UPTIME_BILLING.includes(d.billing))
    {
        content = fmt_money(d.rate);
        tip = `${content} per month`;
    }
    else if (d.bw)
    {
        content = fmt_money(d.rate);
        tip = `${content} per GB`;
    }
    return <Text.xs span key={6} mono tip={tip}>
      {content}
    </Text.xs>;
};

const get_usage = d=>{
    if (UPTIME_BILLING.includes(d.billing))
        return dur_to_str(d.uptime, {sep: ' ', units: 2, precision: 1000});
    return bw_to_readable(d.bw);
};

const get_status = (d, statuses)=><div key={1} className="eadt_status">
    {d.banned
        ? <Icon ico="warn" color="error" title="Not earning" size="xxs"/>
        : <Status_dot loading={!statuses} meta={statuses?.[d.uuid]}/>
    }
    {d.billing==BILLING.FIXED&&<Icon className="eadt_dbr" ico="dbr" size="xxs"
      tip={`${APP_TITLE}+`}/>}
</div>;

function Table_header(){
    return <>
      <TH/>
      <TH left>Device</TH>
      <TH left>Country</TH>
      <TH>Usage</TH>
      <TH>Rate</TH>
      <TH>Amount</TH>
      <TH/>
    </>;
}

function Row({device: d, statuses, on_action, get_actions}){
    const actions = get_actions?.(d) || [];
    const on_action_click = useCallback(id=>{
        on_action?.(id, d);
    }, [on_action, d]);
    const classes = cn({
        eadt_hidden: !!d.hide_ts,
        eadt_banned: !!d.banned,
    });
    const name = get_title(d);
    const name_tip = name==d.title ? '' : d.title;
    return <TR className={classes}>
      <TD left>
        {get_status(d, statuses)}
      </TD>
      <TD left className="eadt_name_td">
        <Text.xs span className="eadt_name" tip={name_tip}>
          {name}
        </Text.xs>
      </TD>
      <TD left>
        {d.country
            ? <Country className="eadt_country" code={d.country} show_code
              is_round/>
            : <Undefined_status/>
          }
      </TD>
      <TD><Text.xs mono>{get_usage(d)}</Text.xs></TD>
      <TD>{get_rate(d)}</TD>
      <TD><Text.xs mono>{fmt_money(d.earned)}</Text.xs></TD>
      <TD className="eadt_actions_td">
        <Context_menu items={actions} on_change={on_action_click}
          ico="dots_vertical" tip="Actions"/>
      </TD>
    </TR>;
}

export function Devices_table({className}){
    const [{list}, loading] = store.use_devices();
    const [show_popup, set_show_popup] = useState(false);
    const [show_hidden_devices, set_show_hidden_devices] = useState(false);
    const [show_rename_popup, set_show_rename_popup] = useState(false);
    const [show_raw_mongo_popup, set_show_raw_mongo_popup] = useState(false);
    const [device, set_device] = useState(null);
    const [raw_device, set_raw_device] = useState(null);
    const is_root = useStore($is_root);
    const toggle_popup = useCallback(()=>set_show_popup(s=>!s), []);
    const handle_click = useCallback(d=>{
        set_device(d);
        toggle_popup();
    }, []);
    const toggle_hidden = useCallback(()=>set_show_hidden_devices(s=>!s),
        []);
    const cleanup = useCallback(()=>{
        set_device(null);
    }, []);
    const close_rename = useCallback(()=>{
        set_show_rename_popup(false);
        set_device(null);
    }, []);
    const close_raw_mongo = useCallback(()=>{
        set_show_raw_mongo_popup(false);
        set_raw_device(null);
        set_device(null);
    }, []);
    const get_actions = useCallback(d=>{
        let actions = [{id: ACTION_TYPES.RENAME, title: 'Rename'}];
        if (d.hide_ts)
            actions.push({id: ACTION_TYPES.UNHIDE, title: 'Unhide'});
        else
            actions.push({id: ACTION_TYPES.HIDE, title: 'Hide'});
        if (APP_ID!='thepiggybox')
            actions.push({id: ACTION_TYPES.DELETE, title: 'Delete'});
        if (is_root)
            actions.push({id: ACTION_TYPES.VIEW_OBJECT, title: 'View Object'});
        return actions;
    }, [is_root]);
    const has_hidden_devices = useMemo(()=>list.some(d=>d.hide_ts), [list]);
    const filtered = useMemo(()=>!has_hidden_devices || show_hidden_devices
        ? list : list.filter(d=>!d.hide_ts)
    , [list, has_hidden_devices, show_hidden_devices]);
    const {page, data, next, prev, total_pages,
        set_page} = use_list_pagination(filtered, {page_size: 20});
    const devices_uuids = useMemo(()=>{
        if (!data)
            return null;
        return data.map(d=>({uuid: d.uuid}));
    }, [data]);
    const statuses = use_device_statuses(devices_uuids);
    const get_mongo_object = useCallback(device_data=>etask(function*(){
        this.on('uncaught', console.error);
        let res;
        try {
            res = yield api.raw_mongo_device(device_data.uuid);
        } catch(e){
            res = {error: e.message};
        }
        if (res.error)
        {
            alert({
                title: 'Error while quering device',
                text: res.error,
                negative: true,
            });
        }
        else
        {
            set_raw_device(JSON.stringify(res.device, null, 4));
            set_show_raw_mongo_popup(true);
        }
    }), []);
    const handle_action = useCallback((id, d)=>{
        switch (id)
        {
        case ACTION_TYPES.RENAME:
            set_device(d);
            set_show_rename_popup(true);
            break;
        case ACTION_TYPES.HIDE:
            hide_device(d);
            break;
        case ACTION_TYPES.UNHIDE:
            unhide_device(d);
            break;
        case ACTION_TYPES.DELETE:
            delete_device(d);
            break;
        case ACTION_TYPES.VIEW_OBJECT:
            get_mongo_object(d);
            break;
        }
    }, []);
    const pagination = useMemo(()=>{
        return {
            page: page-1,
            max: total_pages == 1 ? 0 : total_pages,
            dec: prev,
            inc: next,
            set: set_page,
            total: filtered.length,
        };
    }, [page, total_pages, prev, next, set_page, filtered.length]);
    const render_row = useCallback(d=><Row key={d._id} device={d}
      statuses={statuses} on_action={handle_action}
      get_actions={get_actions}/>, [statuses, handle_action, get_actions]);
    return <div className={cn('ea_devices_table', className)}>
      <div className="eadt_head">
        <Text.xxl h2>Devices</Text.xxl>
        {has_hidden_devices &&
          <Checkbox checked={show_hidden_devices} on_change={toggle_hidden}
            label="Show hidden devices" className="eadt_more"/>
        }
      </div>
      <Table header={<Table_header/>} list={data} loading={loading}
        render_row={render_row} id_key="uuid" pagination={pagination}
        on_row_click={handle_click}/>
      <Popup
        title="Device"
        show={show_popup}
        on_close={toggle_popup}
        on_close_finished={cleanup}
        hide_actions
        hide_header
      >
        {!!device && <Device data={device}/>}
      </Popup>
      {!!device &&
        <Edit_device
          data={device}
          show={show_rename_popup}
          on_close={close_rename}
        />
      }
      {!!raw_device &&
        <Raw_mongo_device
          data={raw_device}
          show={show_raw_mongo_popup}
          on_close={close_raw_mongo}
        />
      }
    </div>;
}
